import { useState, useRef } from 'react';
import Alert from '../common/Alert';
import OccupationInput from '../utils/occupation-input';

const AdditionalDetailsForm = ({ formData, handleInputChange, handleCheckboxChange, handleSubmit }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [pinApiErrors, setPinApiErrors] = useState({
    school: null,
    father: null,
    mother: null
  });

  // Cities state for each location
  const [cities, setCities] = useState({
    school: [],
    father: [],
    mother: []
  });

  // Debounce refs for each PIN code field
  const schoolPinDebounceRef = useRef(null);
  const fatherPinDebounceRef = useRef(null);
  const motherPinDebounceRef = useRef(null);

  // Generic PIN code change handler
  const handlePinCodeChange = (e, type) => {
    let pin = e.target.value.replace(/\D/g, '').slice(0, 6); // digits only, max 6
    const fieldName = `${type}PinCode`;
    
    // Allow only numeric input with max 6 digits
    if (!/^\d{0,6}$/.test(pin)) return;

    // Update formData
    handleInputChange({
      target: { name: fieldName, value: pin }
    });

    // Don't proceed if not 6 digits
    if (pin.length !== 6) {
      handleInputChange({ target: { name: `${type}City`, value: '' } });
      setCities(prev => ({ ...prev, [type]: [] }));
      handleInputChange({ target: { name: `${type}District`, value: '' } });
      handleInputChange({ target: { name: `${type}State`, value: '' } });
      setPinApiErrors(prev => ({ ...prev, [type]: null }));
      return;
    }

    // Get the appropriate debounce ref
    let debounceRef;
    switch (type) {
      case 'school':
        debounceRef = schoolPinDebounceRef;
        break;
      case 'father':
        debounceRef = fatherPinDebounceRef;
        break;
      case 'mother':
        debounceRef = motherPinDebounceRef;
        break;
      default:
        return;
    }

    // Debounce the API call
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
        const data = await res.json();

        if (data[0].Status === "Success" && data[0].PostOffice?.length > 0) {
          const postOffice = data[0].PostOffice[0];
          
          // Update cities list for this type
          setCities(prev => ({
            ...prev,
            [type]: data[0].PostOffice.map(po => po.Name)
          }));
          
          // Auto-fill state, but don't auto-fill city (let user select)
          handleInputChange({ target: { name: `${type}District`, value: postOffice.District } });
          handleInputChange({ target: { name: `${type}State`, value: postOffice.State } });
          setPinApiErrors(prev => ({ ...prev, [type]: null }));
        } else {
          // Clear cities and fields on invalid PIN
          setCities(prev => ({ ...prev, [type]: [] }));
          handleInputChange({ target: { name: `${type}District`, value: '' } });
          handleInputChange({ target: { name: `${type}State`, value: '' } });
          setPinApiErrors(prev => ({ ...prev, [type]: "Invalid PIN code or not found." }));
        }
      } catch (err) {
        // Clear cities and fields on error
        setCities(prev => ({ ...prev, [type]: [] }));
        handleInputChange({ target: { name: `${type}District`, value: '' } });
        handleInputChange({ target: { name: `${type}State`, value: '' } });
        setPinApiErrors(prev => ({ ...prev, [type]: "Failed to fetch District/state for this PIN code." }));
      }
    }, 400);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await handleSubmit(e);
    } catch (error) {
      console.error('Submission failed:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to complete registration';
      setAlert({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="form-section">
      {alert && (
        <Alert 
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert(null)}
        />
      )}
      <p style={{fontSize: 15, fontWeight: 600, color: "green"}}>* Payment Successfully. Please complete the registration.</p>
      <h2>Additional Details</h2>
      
      <div className="form-section-title">School Details</div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="schoolName">School Name *</label>
          <input
            type="text"
            id="schoolName"
            name="schoolName"
            value={formData.schoolName}
            onChange={handleInputChange}
            required
            placeholder="Enter your school name"
          />
        </div>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="schoolPinCode">Pin Code *</label>
          <input
            type="text"
            id="schoolPinCode"
            name="schoolPinCode"
            value={formData.schoolPinCode}
            onChange={(e) => handlePinCodeChange(e, 'school')}
            required
            pattern="[0-9]{6}"
            placeholder="Enter 6-digit PIN code"
          />
          {pinApiErrors.school && (
            <p className="error" style={{marginBottom: '0rem'}}>{pinApiErrors.school}</p>
          )}
        </div>
        
        <div className="form-group">
          <label htmlFor="schoolCity">City/Town/Village *</label>
          <input
            type="text"
            id="schoolCity"
            name="schoolCity"
            value={formData.schoolCity}
            onChange={handleInputChange}
            required
            disabled={!cities.school.length}
            placeholder={!cities.school.length ? "First Enter Pincode" : "Enter your city"}
            list="schoolCities"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="schoolState">District *</label>
          <input
            type="text"
            id="schoolDistrict"
            name="schoolDistrict"
            value={formData.schoolDistrict}
            disabled
            required
            placeholder="Auto-filled from PIN code"
          />
        </div>

        <div className="form-group">
          <label htmlFor="schoolState">State *</label>
          <input
            type="text"
            id="schoolState"
            name="schoolState"
            value={formData.schoolState}
            disabled
            required
            placeholder="Auto-filled from PIN code"
          />
        </div>
      </div>

      <datalist id="schoolCities">
        {cities.school.map((city, index) => (
          <option key={index} value={city}>{city}</option>
        ))}
      </datalist>
      
      <div className="form-section-title">Father's Details</div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="fatherName">Father's Name *</label>
          <input
            type="text"
            id="fatherName"
            name="fatherName"
            value={formData.fatherName}
            onChange={handleInputChange}
            required
            placeholder="Enter father's name"
          />
        </div>
        
        <OccupationInput formData={formData} handleInputChange={handleInputChange} parent={"father"} />
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="fatherPhone">Father's Phone Number (10 Digits) *</label>
          <input
            type="tel"
            id="fatherPhone"
            name="fatherPhone"
            value={formData.fatherPhone}
            onChange={handleInputChange}
            required
            pattern="[0-9]{10}"
            placeholder="Enter father's phone number"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="fatherEmail">Father's Email</label>
          <input
            type="email"
            id="fatherEmail"
            name="fatherEmail"
            value={formData.fatherEmail}
            onChange={handleInputChange}
            placeholder="Enter father's email address"
            required
          />
        </div>
      </div>
      
      <div className="form-group">
        <label htmlFor="fatherAddress">Father's Address *</label>
        <textarea
          id="fatherAddress"
          name="fatherAddress"
          value={formData.fatherAddress}
          onChange={handleInputChange}
          required
          placeholder="Enter father's address"
          rows="3"
        />
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="fatherPinCode">Pin Code *</label>
          <input
            type="text"
            id="fatherPinCode"
            name="fatherPinCode"
            value={formData.fatherPinCode}
            onChange={(e) => handlePinCodeChange(e, 'father')}
            required
            pattern="[0-9]{6}"
            placeholder="Enter 6-digit PIN code"
          />
          {pinApiErrors.father && (
            <p className="error" style={{marginBottom: '0rem'}}>{pinApiErrors.father}</p>
          )}
        </div>
        
        <div className="form-group">
          <label htmlFor="fatherCity">City/Town/Village *</label>
          <input
            type="text"
            id="fatherCity"
            name="fatherCity"
            value={formData.fatherCity}
            onChange={handleInputChange}
            required
            disabled={!cities.father.length}
            placeholder={!cities.father.length ? "First Enter Pincode" : "Enter your City/Town/Village"}
            list="fatherCities"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="fatherDistrict">District *</label>
          <input
            type="text"
            id="fatherDistrict"
            name="fatherDistrict"
            value={formData.fatherDistrict}
            disabled
            required
            placeholder="Auto-filled from PIN code"
          />
        </div>

        <div className="form-group">
          <label htmlFor="fatherState">State *</label>
          <input
            type="text"
            id="fatherState"
            name="fatherState"
            value={formData.fatherState}
            disabled
            required
            placeholder="Auto-filled from PIN code"
          />
        </div>
      </div>

      <datalist id="fatherCities">
        {cities.father.map((city, index) => (
          <option key={index} value={city}>{city}</option>
        ))}
      </datalist>
      
      <div className="form-section-title">Mother's Details</div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="motherName">Mother's Name *</label>
          <input
            type="text"
            id="motherName"
            name="motherName"
            value={formData.motherName}
            onChange={handleInputChange}
            required
            placeholder="Enter mother's name"
          />
        </div>
        
        <OccupationInput formData={formData} handleInputChange={handleInputChange} parent={"mother"} />
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="motherPhone">Mother's Phone Number (10 Digits) *</label>
          <input
            type="tel"
            id="motherPhone"
            name="motherPhone"
            value={formData.motherPhone}
            onChange={handleInputChange}
            required
            pattern="[0-9]{10}"
            placeholder="Enter mother's phone number"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="motherEmail">Mother's Email *</label>
          <input
            type="email"
            id="motherEmail"
            name="motherEmail"
            value={formData.motherEmail}
            onChange={handleInputChange}
            placeholder="Enter mother's email address"
            required
          />
        </div>
      </div>

      <div className="form-group">
        <label style={{display: 'flex', gap: '0.5rem', justifyContent:'flex-start', alignItems:'center'}}>
          <input type="checkbox" style={{width:'15px'}} onChange={handleCheckboxChange} />
          Same as Father's Address
        </label>
      </div>
      
      <div className="form-group">
        <label htmlFor="motherAddress">Mother's Address *</label>
        <textarea
          id="motherAddress"
          name="motherAddress"
          value={formData.motherAddress}
          onChange={handleInputChange}
          required
          placeholder="Enter mother's address"
          rows="3"
        />
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="motherPinCode">Pin Code *</label>
          <input
            type="text"
            id="motherPinCode"
            name="motherPinCode"
            value={formData.motherPinCode}
            onChange={(e) => handlePinCodeChange(e, 'mother')}
            required
            pattern="[0-9]{6}"
            placeholder="Enter 6-digit PIN code"
          />
          {pinApiErrors.mother && (
            <p className="error" style={{marginBottom: '0rem'}}>{pinApiErrors.mother}</p>
          )}
        </div>
        
        <div className="form-group">
          <label htmlFor="motherCity">City/Town/Village *</label>
          <input
            type="text"
            id="motherCity"
            name="motherCity"
            value={formData.motherCity}
            onChange={handleInputChange}
            required
            disabled={!cities.mother.length}
            placeholder={!cities.mother.length ? "First Enter Pincode" : "Enter your City/Town/Village"}
            list="motherCities"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="motherDistrict">District *</label>
          <input
            type="text"
            id="motherDistrict"
            name="motherDistrict"
            value={formData.motherDistrict}
            disabled
            required
            placeholder="Auto-filled from PIN code"
          />
        </div>

        <div className="form-group">
          <label htmlFor="motherState">State *</label>
          <input
            type="text"
            id="motherState"
            name="motherState"
            value={formData.motherState}
            disabled
            required
            placeholder="Auto-filled from PIN code"
          />
        </div>
      </div>

      <datalist id="motherCities">
        {cities.mother.map((city, index) => (
          <option key={index} value={city}>{city}</option>
        ))}
      </datalist>
      
      <div className="form-section-title">Other Information</div>
      <div className="form-group">
        <label htmlFor="referenceSource">How did you hear about us? *</label>
        <select
          id="referenceSource"
          name="referenceSource"
          value={formData.referenceSource}
          onChange={handleInputChange}
          required
        >
          <option value="">Select an option</option>
          <option value="friend">Friend or Family</option>
          <option value="social_media">Social Media</option>
          <option value="advertisement">Advertisement</option>
          <option value="school">School</option>
          <option value="event">Event</option>
          <option value="Old Student of Alok Bhaiya">Old Student of Alok Bhaiya</option>
          <option value="other">Other</option>
        </select>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="photo">Passport Size Photo *</label>
          <input
            type="file"
            id="photo"
            name="photo"
            onChange={handleInputChange}
            required
            accept="image/*"
          />
          <small>Upload a passport size photo (JPEG or PNG, max 2MB)</small>
        </div>
        
        <div className="form-group">
          <label htmlFor="aadharPdf">Aadhar Card PDF *</label>
          <input
            type="file"
            id="aadharPdf"
            name="aadharPdf"
            onChange={handleInputChange}
            required
            accept="application/pdf"
          />
          <small>Upload your Aadhar card as PDF (max 5MB)</small>
        </div>
      </div>
      
      <div className="form-actions">
        <button 
          type="submit" 
          className="btn btn-success"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-small"></span>
              Processing...
            </>
          ) : (
            'Complete Registration'
          )}
        </button>
      </div>
    </form>
  );
};

export default AdditionalDetailsForm;